/**
 * Breadcrumb navigator — resolves a breadcrumb label to a target path.
 *
 * Used by LoggedInLayout's click-delegation handler so existing breadcrumbs
 * across pages become clickable without modifying any per-page JSX.
 *
 * The map is **per-role** because URL structure differs between role
 * surfaces: e.g. admin's "Content Management" lives at
 * `/admin/content/management`, but super-admin's lives at
 * `/superadmin/content/content-management`. Resolution flow:
 *
 *   1. derive the role prefix from the current pathname
 *   2. look up the normalized crumb label in that role's table
 *   3. if no match, fall back to the role's dashboard/home (always safe)
 *
 * Last-child crumbs are skipped by the caller (treated as the current
 * page), so this map only needs to cover navigable section labels.
 */

const ROLE_PREFIXES = ['/admin', '/superadmin', '/trainer', '/trainee', '/learner', '/manager'];

export const getRolePrefix = (pathname: string): string => {
  for (const prefix of ROLE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return prefix;
  }
  return '';
};

const normalize = (label: string): string =>
  label.toLowerCase().replace(/[\s/&-]+/g, ' ').replace(/[^a-z0-9 ]/g, '').trim();

/**
 * Sidebar section/group headings — these are pure visual groupings inside the
 * sidebar (see the `<div className="logged-in-section-title">` blocks in
 * Siderbar.tsx). They don't correspond to a specific destination page, so when
 * they appear as a breadcrumb crumb (typically the first one) they should be
 * non-actionable: no navigation, no pointer cursor, no hover affordance.
 */
export const SIDEBAR_SECTION_LABELS: ReadonlySet<string> = new Set([
  'main menu',
  'menu',
  'learnings',
  'learn',
  'tools',
  'features on demand',
  'features',
  'content management',
  'content',
  'user management',
  'settings',
  'sys',
  'system',
  'manager tools',
  'mgr',
]);

export const isSidebarSectionLabel = (label: string): boolean =>
  SIDEBAR_SECTION_LABELS.has(normalize(label));

/**
 * Per-role mapping: normalized breadcrumb label → absolute target path.
 * Mirrors the sidebar configuration in `Siderbar.tsx` so a click on
 * a section/page label lands on the exact URL the sidebar item would
 * have used.
 */
const ROLE_LABEL_PATHS: Record<string, Record<string, string>> = {
  '/admin': {
    'dashboard': '/admin/dashboard',
    'admin': '/admin/dashboard',
    'users': '/admin/users/list',
    'user': '/admin/users/list',
    'user management': '/admin/users/list',
    'my team': '/admin/users/team',
    'assignments': '/admin/assignments',

    'content management': '/admin/content/management',
    'course management': '/admin/content/courses',
    'catalog category': '/admin/content/catalog',
    'catalog': '/admin/content/catalog',
    'category': '/admin/content/catalog',
    'group projects': '/admin/content/group-projects',
    'group project': '/admin/content/group-projects',
    'certification management': '/admin/content/certifications',
    'certifications': '/admin/content/certifications',
    'library': '/admin/content/management',

    'reports': '/admin/reports',
    'reports analytics': '/admin/reports',
    'report and analytics': '/admin/reports',
    'analytics': '/admin/reports',
    'custom report builder': '/admin/reports',

    'tools': '/admin/tools/bulk-uploader',
    'bulk uploader': '/admin/tools/bulk-uploader',
    'feedback': '/admin/tools/feedback',
    'survey and assessment': '/admin/tools/feedback',
    'marketing': '/admin/tools/marketing',
    'marketing notifications': '/admin/tools/marketing',
    'ilt resources': '/admin/tools/ilt-resources',
    'ilt resource management': '/admin/tools/ilt-resources',
    'knowledge base': '/admin/tools/knowledge-base',

    'settings': '/admin/account-settings',
    'account settings': '/admin/account-settings',
    'api keys': '/admin/settings/api-keys',
    'support help': '/admin/support',
    'support': '/admin/support',
  },

  '/superadmin': {
    'dashboard': '/superadmin/dashboard',
    'super admin': '/superadmin/dashboard',
    'superadmin': '/superadmin/dashboard',
    'organization insights': '/superadmin/analytics/organization-insights',
    'analytics': '/superadmin/analytics/organization-insights',

    'users': '/superadmin/users/user-list',
    'user': '/superadmin/users/user-list',
    'user management': '/superadmin/users/user-list',
    'my team': '/superadmin/users/my-team',
    'performance tracking': '/superadmin/users/performance-tracking',
    'learning assignment tool': '/superadmin/users/learning-assignment',
    'security and permissions': '/superadmin/users/security',

    'content architect': '/superadmin/content/catalog',
    'content management': '/superadmin/content/content-management',
    'course management': '/superadmin/content/course-management',
    'catalog category': '/superadmin/content/catalog',
    'catalog': '/superadmin/content/catalog',
    'category': '/superadmin/content/catalog',
    'assessments': '/superadmin/content/assessments',
    'group projects': '/superadmin/content/group-projects',
    'group project': '/superadmin/content/group-projects',
    'certification management': '/superadmin/content/certifications',
    'certifications': '/superadmin/content/certifications',
    'training assistant': '/superadmin/content/training-assistant',
    'library': '/superadmin/content/content-management',

    'reports': '/superadmin/reports',
    'reports analytics': '/superadmin/reports',
    'report and analytics': '/superadmin/reports',
    'custom report builder': '/superadmin/reports',

    'tools': '/superadmin/tools/bulk-uploader',
    'bulk uploader': '/superadmin/tools/bulk-uploader',
    'feedback': '/superadmin/tools/feedback',
    'feedback and survey': '/superadmin/tools/feedback',
    'survey and assessment': '/superadmin/tools/feedback',
    'marketing': '/superadmin/tools/marketing',
    'marketing notifications': '/superadmin/tools/marketing',
    'ilt resources': '/superadmin/tools/ilt-resources',
    'ilt resource management': '/superadmin/tools/ilt-resources',
    'knowledge base': '/superadmin/tools/knowledge-base',

    'settings': '/superadmin/account-settings',
    'account settings': '/superadmin/account-settings',
    'api keys': '/superadmin/settings/api-keys',
    'organizations': '/superadmin/settings/organizations',
    'support help': '/superadmin/support',
    'support': '/superadmin/support',
  },

  '/trainer': {
    'dashboard': '/trainer/dashboard',
    'trainer': '/trainer/dashboard',

    'course management': '/trainer/content/course-management',
    'assessment': '/trainer/content/assessments',
    'assessments': '/trainer/content/assessments',
    'group project': '/trainer/content/group-projects',
    'group projects': '/trainer/content/group-projects',
    'content management': '/trainer/content/course-management',

    'users': '/trainer/users/user-list',
    'user': '/trainer/users/user-list',
    'user management': '/trainer/users/user-list',
    'my team': '/trainer/users/my-team',
    'learning assignment tool': '/trainer/users/learning-assignment',
    'performance': '/trainer/users/performance',

    'reports': '/trainer/reports',

    'tools': '/trainer/tools/marketing',
    'marketing': '/trainer/tools/marketing',
    'marketing notification': '/trainer/tools/marketing',
    'ilt resources': '/trainer/tools/ilt-resources',
    'ilt resource management': '/trainer/tools/ilt-resources',

    'settings': '/trainer/account-settings',
    'account settings': '/trainer/account-settings',
    'support help': '/trainer/support',
    'support': '/trainer/support',
    'knowledge base': '/knowledge-base',
  },

  '/trainee': {
    'home': '/trainee/home',
    'dashboard': '/trainee/home',
    'trainee': '/trainee/home',
    'transcript': '/trainee/transcript',

    'my learnings': '/trainee/my-learnings/courses',
    'courses': '/trainee/my-learnings/courses',
    'browse courses': '/trainee/my-learnings/courses?tab=browse',
    'my course': '/trainee/my-learnings/courses',
    'my courses': '/trainee/my-learnings/courses',
    'wishlist': '/trainee/my-learnings/wishlist',

    'achievements': '/trainee/achievements',
    'certifications': '/trainee/certifications',
    'my team': '/trainee/team',
    'team': '/trainee/team',

    'ai learning paths': '/trainee/ai-paths',
    'support help': '/trainee/support',
    'support': '/trainee/support',
    'account settings': '/trainee/account-settings',
    'knowledge base': '/knowledge-base',
  },

  '/learner': {
    'assignments': '/learner/assignments',
    'home': '/trainee/home',
    'dashboard': '/trainee/home',
  },

  '/manager': {
    'dashboard': '/manager/dashboard',
    'manager': '/manager/dashboard',
    'my teams': '/manager/teams',
    'teams': '/manager/teams',
    'assignments': '/manager/assignments',
  },
};

/**
 * Resolve a clicked breadcrumb to a navigable path. Returns null if the
 * label is empty; otherwise returns either an exact mapped target or a
 * safe role-default fallback.
 */
export const resolveBreadcrumbTarget = (
  label: string,
  currentPathname: string
): string | null => {
  const trimmed = label.trim();
  if (!trimmed) return null;

  // Sidebar section/group labels are not destinations — the breadcrumb
  // shows them as hierarchy hints only.
  if (isSidebarSectionLabel(trimmed)) return null;

  const rolePrefix = getRolePrefix(currentPathname);
  const key = normalize(trimmed);

  const roleMap = rolePrefix ? ROLE_LABEL_PATHS[rolePrefix] : undefined;
  const exact = roleMap?.[key];
  if (exact) return exact;

  // Safe fallback — role's home/dashboard. Trainee/learner home is /trainee/home.
  if (rolePrefix === '/trainee' || rolePrefix === '/learner') return '/trainee/home';
  if (rolePrefix) return `${rolePrefix}/dashboard`;
  return '/';
};
